import {
  BehaviorSubject,
  defer,
  EMPTY,
  finalize,
  filter,
  firstValueFrom,
  map,
  Observable,
  shareReplay,
} from 'rxjs';
import { CurrentUserInfo } from './user-information.model';
import { CurrentUserInfoResponse, UserProfileClient } from '../../openapi/identity';
import { LocalStorageService } from '../storage/local-storage.service';
import { catchError } from 'rxjs/operators';
import { Injectable } from '@angular/core';

const CURRENT_ACCOUNT_KEY = 'current_account';

interface IUserAccount {
  permissions: string[];
}

@Injectable({
  providedIn: 'root',
})
export class UserInformationService {
  private readonly currentUser$: BehaviorSubject<CurrentUserInfo | null | undefined> =
    new BehaviorSubject<CurrentUserInfo | null | undefined>(undefined);

  private readonly currentAccount$: BehaviorSubject<IUserAccount | null> =
    new BehaviorSubject<IUserAccount | null>(null);

  public readonly userPermissions$: Observable<string[]> = defer(() => this.getCurrentUser()).pipe(
    filter((userInfo) => userInfo !== undefined),
    map((userInfo): string[] => userInfo?.permissions ?? []),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  private isUserLoading = false;

  constructor(
    private readonly userProfileClient: UserProfileClient,
    private readonly localStorageService: LocalStorageService,
  ) {}

  public get currentAccount(): IUserAccount | null {
    return this.currentAccount$.value;
  }

  public getCurrentUser(): Observable<CurrentUserInfo | null | undefined> {
    if (!this.currentUser$.value && !this.isUserLoading) {
      this.loadCurrentUser().then();
    }

    return this.currentUser$.asObservable();
  }

  public loadCurrentUser(): Promise<void> {
    this.isUserLoading = true;

    return firstValueFrom(
      this.userProfileClient.userProfileGet().pipe(
        finalize(() => (this.isUserLoading = false)),
        map((user: CurrentUserInfoResponse) => {
          const currentUser: CurrentUserInfo = {
            id: user.id,
            userName: user.userName,
            email: user.email,
            isAuthenticatorEnabled: user.isAuthenticatorEnabled,
            isAdmin: user.permissions?.length !== undefined && user.permissions?.length > 0,
            permissions: user.permissions ?? [],
          };

          this.isUserLoading = false;
          this.currentUser$.next(currentUser);

          const currentAccount = this.localStorageService.get(CURRENT_ACCOUNT_KEY);

          this.setCurrentAccount(currentAccount ? JSON.parse(currentAccount) : null);
        }),
        catchError(() => {
          this.currentUser$.next(null);
          this.setCurrentAccount(null);

          return EMPTY;
        }),
      ),
    );
  }

  public getCurrentAccount(): Observable<IUserAccount | null> {
    return this.currentAccount$.asObservable();
  }

  public setCurrentAccount(account: IUserAccount | null): void {
    this.localStorageService.set(CURRENT_ACCOUNT_KEY, JSON.stringify(account));
    this.currentAccount$.next(account);
  }

  public removeSavedAccount(): void {
    this.localStorageService.remove(CURRENT_ACCOUNT_KEY);
  }
}
